import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, CheckCircle, X } from 'lucide-react';
import { MANUAL_DEPOSIT_PAYMENT_METHODS } from './MarkDepositPaidModal';

export type ManualInvoicePaymentPayload = {
  paymentMethod: string;
  paymentDate: string;
  referenceNumber?: string | null;
  notes?: string | null;
};

export interface MarkInvoicePaidModalProps {
  isOpen: boolean;
  amountDue: number;
  onClose: () => void;
  onSubmit: (payload: ManualInvoicePaymentPayload) => Promise<void>;
}

const MarkInvoicePaidModal: React.FC<MarkInvoicePaidModalProps> = ({
  isOpen,
  amountDue,
  onClose,
  onSubmit,
}) => {
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentDate, setPaymentDate] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitInFlightRef = useRef(false);

  useEffect(() => {
    if (!isOpen) return;
    setPaymentMethod('');
    setPaymentDate(new Date().toISOString().slice(0, 10));
    setReferenceNumber('');
    setNotes('');
    setIsSubmitting(false);
    submitInFlightRef.current = false;
  }, [isOpen, amountDue]);

  if (!isOpen) return null;

  const formattedAmount = Number.isFinite(amountDue) ? amountDue.toFixed(2) : '0.00';

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const handleSubmit = async () => {
    if (isSubmitting || submitInFlightRef.current) return;
    if (!paymentMethod.trim()) return;

    submitInFlightRef.current = true;
    setIsSubmitting(true);
    try {
      await onSubmit({
        paymentMethod: paymentMethod.trim(),
        paymentDate: paymentDate || new Date().toISOString().slice(0, 10),
        referenceNumber: referenceNumber.trim() || null,
        notes: notes.trim() || null,
      });
      onClose();
    } catch {
      // Caller handles toast/errors; keep modal open for retry.
    } finally {
      setIsSubmitting(false);
      submitInFlightRef.current = false;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[2000] flex items-start sm:items-center justify-center p-4 pt-20 sm:pt-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full border border-gray-200 overflow-hidden max-h-[calc(100vh-6rem)] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 shrink-0 bg-gray-50">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <i className="bi bi-cash-coin text-green-600 text-xl" aria-hidden />
            <span>Mark Invoice Paid</span>
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={isSubmitting}
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 flex-1 min-h-0 overflow-y-auto space-y-4">
          <p className="text-sm text-gray-600">
            Record how the payment of <span className="font-semibold text-gray-900">${formattedAmount}</span> was received.
          </p>

          <div>
            <label htmlFor="mark-invoice-amount" className="block text-sm font-medium text-gray-700 mb-1">
              Amount Paid
            </label>
            <input
              id="mark-invoice-amount"
              type="text"
              readOnly
              value={`$${formattedAmount}`}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-900 font-semibold"
            />
          </div>

          <div>
            <label htmlFor="mark-invoice-payment-method" className="block text-sm font-medium text-gray-700 mb-1">
              Payment Method <span className="text-red-600">*</span>
            </label>
            <select
              id="mark-invoice-payment-method"
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              disabled={isSubmitting}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white"
            >
              <option value="">Select payment method</option>
              {MANUAL_DEPOSIT_PAYMENT_METHODS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="mark-invoice-payment-date" className="block text-sm font-medium text-gray-700 mb-1">
              Payment Date <span className="text-red-600">*</span>
            </label>
            <input
              id="mark-invoice-payment-date"
              type="date"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              disabled={isSubmitting}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>

          <div>
            <label htmlFor="mark-invoice-reference-number" className="block text-sm font-medium text-gray-700 mb-1">
              Reference Number <span className="text-gray-400 font-normal">(Optional)</span>
            </label>
            <input
              id="mark-invoice-reference-number"
              type="text"
              value={referenceNumber}
              onChange={(e) => setReferenceNumber(e.target.value)}
              disabled={isSubmitting}
              placeholder="Check number, transaction ID, etc."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>

          <div>
            <label htmlFor="mark-invoice-notes" className="block text-sm font-medium text-gray-700 mb-1">
              Notes <span className="text-gray-400 font-normal">(Optional)</span>
            </label>
            <textarea
              id="mark-invoice-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={isSubmitting}
              rows={3}
              placeholder="Internal notes about this payment"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 shrink-0">
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Go Back</span>
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting || !paymentMethod.trim() || !paymentDate}
            className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 bg-green-600 hover:bg-green-700"
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Processing...</span>
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4" />
                <span>Mark Invoice Paid</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MarkInvoicePaidModal;
